import { useEffect, useMemo, useRef } from "react";
import { CustomChart } from "echarts/charts";
import type { CustomSeriesOption } from "echarts/charts";
import { DataZoomComponent, GridComponent, MarkAreaComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import type { EChartsCoreOption } from "echarts/core";
import { SVGRenderer } from "echarts/renderers";

import type { NarrowPassageDirection, NarrowPassageListItem } from "./api";
import { displayLocale } from "./localization";
import { vehicleClassColor } from "./vehicleClasses";

echarts.use([CustomChart, DataZoomComponent, GridComponent, MarkAreaComponent, SVGRenderer, TooltipComponent]);

const lanes: NarrowPassageDirection[] = ["1_to_2", "2_to_1"];
// Overlapping passages of one lane are spread over a few rows so that a queue stays readable.
const rowsPerLane = 3;
const minimumWindowMS = 15 * 60 * 1000;
const otherClassColor = "#949a9e";

type Span = { state: "provisional" | "revised"; start: number; end: number };
type RenderResult = ReturnType<NonNullable<CustomSeriesOption["renderItem"]>>;
type RenderChild = { type: "line" | "rect" | "circle"; shape: Record<string, number>; style: Record<string, unknown> };
type ZoomRange = { start?: number; end?: number; startValue?: number; endValue?: number };
type Placed = { item: NarrowPassageListItem; lane: number; row: number; start: number; end: number };

// Counted passages are drawn; discarded rows stay in the list of g only.
function drawnPassages(items: NarrowPassageListItem[]) {
  return items.filter((item) => item.status !== "discarded" && item.direction && (item.outer_ms ?? item.inbound_ms ?? item.outbound_ms) !== undefined);
}

function place(items: NarrowPassageListItem[]) {
  const rowEnds = lanes.map(() => Array.from({ length: rowsPerLane }, () => -Infinity));
  return drawnPassages(items).map((item): Placed => {
    const start = item.outer_ms ?? item.inbound_ms ?? item.outbound_ms ?? 0;
    const end = item.outbound_ms ?? item.inbound_ms ?? item.outer_ms ?? start;
    const lane = lanes.indexOf(item.direction as NarrowPassageDirection);
    const ends = rowEnds[lane];
    const free = ends.findIndex((value) => value < start);
    const row = free >= 0 ? free : ends.indexOf(Math.min(...ends));
    ends[row] = Math.max(ends[row], end);
    return { item, lane, row, start, end };
  }).sort((left, right) => left.start - right.start);
}

export function NarrowPassageSequenceChart({ items, dayStart, dayEnd, timeZone, spans, windowStart, windowEnd, label, laneLabels, describe, onWindowChange }: {
  items: NarrowPassageListItem[];
  dayStart: number;
  dayEnd: number;
  timeZone: string;
  spans: Span[];
  windowStart: number;
  windowEnd: number;
  label: string;
  laneLabels: Record<NarrowPassageDirection, string>;
  describe: (item: NarrowPassageListItem) => string;
  onWindowChange: (start: number, end: number) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof echarts.init> | null>(null);
  const current = useRef({ windowStart, windowEnd, dayStart, dayEnd, onWindowChange });
  useEffect(() => {
    current.current = { windowStart, windowEnd, dayStart, dayEnd, onWindowChange };
  });
  const placed = useMemo(() => place(items), [items]);
  const daySpans = useMemo(() => spans.filter((span) => span.end > dayStart && span.start < dayEnd), [dayEnd, dayStart, spans]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const chart = echarts.init(element, undefined, { renderer: "svg" });
    instance.current = chart;
    // The slider reports percentages of the day, the window buttons absolute values.
    chart.on("datazoom", (event) => {
      const payload = event as ZoomRange & { batch?: ZoomRange[] };
      const range = payload.batch?.[0] ?? payload;
      const state = current.current;
      const value = (absolute?: number, percent?: number) => absolute ?? (percent === undefined ? undefined : state.dayStart + (state.dayEnd - state.dayStart) * percent / 100);
      const start = value(range.startValue, range.start);
      const end = value(range.endValue, range.end);
      if (start === undefined || end === undefined) return;
      const rounded = [start, end].map((item) => Math.round(item / 60000) * 60000);
      if (rounded[0] !== state.windowStart || rounded[1] !== state.windowEnd) state.onWindowChange(rounded[0], rounded[1]);
    });
    const resize = () => chart.resize();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    observer?.observe(element);
    if (!observer) window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      if (!observer) window.removeEventListener("resize", resize);
      instance.current = null;
      chart.dispose();
    };
  }, []);

  const option = useMemo<EChartsCoreOption>(() => {
    const time = new Intl.DateTimeFormat(displayLocale, { hour: "2-digit", minute: "2-digit", timeZone });
    const renderItem: CustomSeriesOption["renderItem"] = (params, api) => {
      const entry = placed[params.dataIndex];
      const band = Number((api.size?.([0, 1]) as number[] | undefined)?.[1] ?? 60);
      const rowHeight = band * 0.84 / rowsPerLane;
      const y = api.coord([entry.start, entry.lane])[1] - band * 0.42 + (entry.row + 0.5) * rowHeight;
      const x = (value: number) => api.coord([value, entry.lane])[0];
      const truck = entry.item.class === "truck";
      const color = truck ? vehicleClassColor("truck") : otherClassColor;
      const opacity = truck ? 1 : 0.55;
      const height = rowHeight * (truck ? 0.8 : 0.5);
      const { outer_ms: outer, inbound_ms: inbound, outbound_ms: outbound } = entry.item;
      const children: RenderChild[] = [];
      // Thin line: waiting between the outer and the inner line on the way in.
      if (outer !== undefined && inbound !== undefined) children.push({ type: "line", shape: { x1: x(outer), y1: y, x2: x(inbound), y2: y }, style: { stroke: color, lineWidth: 1.5, lineDash: [3, 2], opacity } });
      if (inbound !== undefined && outbound !== undefined) {
        children.push({ type: "rect", shape: { x: x(inbound), y: y - height / 2, width: Math.max(2, x(outbound) - x(inbound)), height }, style: { fill: color, opacity } });
      } else {
        // One-sided passage: a filled marker for a seen inbound, an open one for a seen outbound.
        const at = inbound ?? outbound ?? outer ?? entry.start;
        children.push({ type: "circle", shape: { cx: x(at), cy: y, r: Math.max(3, height / 2) }, style: inbound !== undefined ? { fill: color, opacity } : { fill: "#ffffff", stroke: color, lineWidth: 1.5, opacity } });
      }
      return { type: "group", children } as RenderResult;
    };
    return {
      animation: false,
      grid: { top: 12, right: 16, bottom: 74, left: 118, containLabel: false },
      tooltip: { trigger: "item", renderMode: "richText", confine: true, formatter: (parameters: { dataIndex?: number }) => placed[parameters.dataIndex ?? -1] ? describe(placed[parameters.dataIndex ?? 0].item) : "", textStyle: { fontFamily: "IBM Plex Sans", fontSize: 11 } },
      xAxis: {
        type: "time",
        min: dayStart,
        max: dayEnd,
        axisLine: { lineStyle: { color: "#cfd2d6" } },
        axisTick: { show: false },
        axisLabel: { color: "#949a9e", fontFamily: "IBM Plex Sans", fontSize: 9, hideOverlap: true, formatter: (value: number) => time.format(value) },
        splitLine: { show: true, lineStyle: { color: "#e4e7e9", type: "dashed" } },
      },
      yAxis: {
        type: "category",
        inverse: true,
        data: lanes.map((lane) => laneLabels[lane]),
        axisLine: { lineStyle: { color: "#cfd2d6" } },
        axisTick: { show: false },
        axisLabel: { color: "#707070", fontFamily: "IBM Plex Sans", fontSize: 10 },
        splitLine: { show: true, lineStyle: { color: "#e4e7e9" } },
      },
      dataZoom: [{
        type: "slider",
        xAxisIndex: 0,
        filterMode: "none",
        minValueSpan: minimumWindowMS,
        maxValueSpan: dayEnd - dayStart,
        bottom: 8,
        height: 28,
        showDetail: true,
        showDataShadow: false,
        borderColor: "#cfd2d6",
        backgroundColor: "#f3f3f3",
        fillerColor: "rgba(56, 124, 160, 0.16)",
        handleStyle: { color: "#ffffff", borderColor: "#387ca0" },
        moveHandleStyle: { color: "#387ca0", opacity: 0.4 },
        textStyle: { color: "#707070", fontFamily: "IBM Plex Sans", fontSize: 9 },
        labelFormatter: (value: number) => time.format(value),
      }],
      series: [{
        type: "custom",
        name: label,
        clip: true,
        encode: { x: [0, 2], y: 1 },
        data: placed.map((entry) => [entry.start, entry.lane, entry.end]),
        renderItem,
        markArea: {
          silent: true,
          label: { show: false },
          data: daySpans.map((span) => [
            { xAxis: Math.max(span.start, dayStart), itemStyle: { color: span.state === "provisional" ? "rgba(159, 15, 39, 0.08)" : "rgba(56, 124, 160, 0.1)" } },
            { xAxis: Math.min(span.end, dayEnd) },
          ]),
        },
      } satisfies CustomSeriesOption],
    };
  }, [dayEnd, dayStart, daySpans, describe, label, laneLabels, placed, timeZone]);

  useEffect(() => {
    instance.current?.setOption(option, { notMerge: true });
  }, [option]);

  // A new option resets the zoom, so the window is applied after every option as well.
  useEffect(() => {
    instance.current?.dispatchAction({ type: "dataZoom", dataZoomIndex: 0, startValue: windowStart, endValue: windowEnd });
  }, [option, windowEnd, windowStart]);

  return <div ref={container} className="group-sequence-canvas" role="img" aria-label={label} data-window-start={new Date(windowStart).toISOString()} data-window-end={new Date(windowEnd).toISOString()} data-drawn={placed.length} data-marked-windows={daySpans.length} />;
}
